import { useErrors } from '@/Composables/errors'
import { settle } from '@/utils/promises'
import { watchIgnorable } from '@vueuse/core'
import { ref, onMounted, computed } from 'vue'
import { getHeadersWithToken, handleResponse } from '@/utils/http'
import { getAllSortedColumns, onlySortData, updateColumnSort } from '@/Components/DataGrid/utils'
import { useLocalStorage } from '@vueuse/core'
import { useAccessUser } from '@/Composables/access-user'

const LANG = {
  FAILED_TO_LOAD_FILTERS: 'Failed to load filters. The page will now reload.',
  FAILED_TO_SAVE_FILTER: 'Failed to save filter'
}

const api = {
  saveFiltersAbortController: ref(new AbortController()),

  loadFilters: async (table) => {
    const endpoint = route('api.savedFilters.index')

    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: getHeadersWithToken(),
      body: JSON.stringify({ table })
    })

    return handleResponse('loadSavedFilters', resp)
  },

  saveFilters: async (table, filters) => {
    const endpoint = route('api.savedFilters.save')

    api.saveFiltersAbortController.value.abort()
    api.saveFiltersAbortController.value = new AbortController()

    try {
      const resp = await fetch(endpoint, {
        signal: api.saveFiltersAbortController.value.signal,
        method: 'POST',
        headers: getHeadersWithToken(),
        body: JSON.stringify({ table, filters })
      })

      return handleResponse('saveFilters', resp)
    } catch (err) {
      if (err.name !== 'AbortError') {
        throw err
      }
    }
  }
}

const createSaveState = async ({ gridApi, onSave, filterName }) => {
  const currentTableFilters = gridApi.value.getFilterModel()

  const currentTableSort = onlySortData(getAllSortedColumns(gridApi))

  const customFilters = onSave ? await onSave(currentTableFilters) : []

  // All AG grid filters excluding any custom overrides provided by the save hook.
  const tableFilters = Object.fromEntries(
    Object.entries(currentTableFilters).filter(([filterName]) => !customFilters[filterName])
  )

  const filters = {
    state: {
      filters: {
        agGrid: tableFilters,
        custom: customFilters
      },
      sort: {
        agGrid: currentTableSort
      }
    },
    name: filterName,
    id: `${filterName}-${Date.now()}`,
    createdAt: Date.now(),
    version: 1
  }

  return filters
}

const loadState = async ({ filter, gridApi, onPreLoad, onPostLoad }) => {
  if (onPreLoad) {
    await onPreLoad(filter)
  }
  gridApi.value.setFilterModel(filter.state.filters.agGrid)
  updateColumnSort(gridApi, filter.state.sort.agGrid)
  if (onPostLoad) {
    await onPostLoad(filter)
  }
}

export const useUsersSavedFilters = ({ key, gridApi, onSave, onPostLoad, onPreLoad }) => {
  if (!gridApi) {
    throw new Error('Grid API is required.')
  }

  const storage = useRemoteFilterStorage(key)

  const remove = (filter) => {
    storage.value = storage.value.filter((f) => f.id !== filter.id)
  }

  const load = async (filter) => {
    await loadState({ filter, gridApi, onPreLoad, onPostLoad })
  }

  const save = async ({ filterName }) => {
    const filters = await createSaveState({ filterName, gridApi, onSave })

    const existingFilter = storage.value.find((f) => f.name === filters.name)

    if (existingFilter) remove(existingFilter)

    storage.value = [...storage.value, filters]
  }

  return {
    save,
    remove,
    load,
    data: storage
  }
}

export const useStickyFilters = ({ key, gridApi, onSave, onPostLoad, onPreLoad }) => {
  const storage = useLocalFilterStorage(key)
  const defaultState = ref({})

  const captureDefaultState = async () => {
    defaultState.value = await createSaveState({ filterName: key, gridApi, onSave })
  }

  const isInDefaultState = computed(() => {
    if (!storage.value.name) {
      return true
    }
    return JSON.stringify(defaultState.value.state) === JSON.stringify(storage.value.state)
  })

  const load = async ({ captureDefault = true } = {}) => {
    if (captureDefault) {
      await captureDefaultState()
    }
    if (storage.value.name) {
      await loadState({ filter: storage.value, gridApi, onPreLoad, onPostLoad })
      return storage.value
    }
    return {}
  }

  const save = async () => {
    storage.value = await createSaveState({ filterName: key, gridApi, onSave })
  }

  return {
    save,
    load,
    isInDefaultState
  }
}

export const useRemoteFilterStorage = (key) => {
  const { showHardError, showError } = useErrors()
  const data = ref([])

  onMounted(async () => {
    const [err, result] = await settle(api.loadFilters(key))

    // This is a hard error because modifying saved filters would
    // delete any other saved filters that failed to load.
    if (err) {
      showHardError({ message: LANG.FAILED_TO_LOAD_FILTERS })
      return
    }

    const [_, loadedFilters] = result
    data.value = loadedFilters

    const { ignoreUpdates } = watchIgnorable(
      () => [...data.value],
      async (filters, oldFilters) => {
        const [err, _] = await settle(api.saveFilters(key, filters))
        if (err) {
          ignoreUpdates(() => {
            data.value = oldFilters
          })
          showError({ message: LANG.FAILED_TO_SAVE_FILTER, useToast: true })
        }
      }
    )
  })

  return data
}

export const useLocalFilterStorage = (key) => {
  const { user } = useAccessUser()
  return useLocalStorage(`pf.v1.user.${user.value.id}.${key}`, {})
}
